// The position, drawn by the game's own renderer.
//
// **Rebuilt from the picture, so it is a reconstruction** — see `asciiBoard.ts`
// for exactly what the drawing cannot carry. Facing, held material and lane
// ownership are gone; where the pieces stand is not.
//
// Keyed by the frame it is showing, so picking a different position rebuilds
// the canvas rather than trying to diff one board into another — these are
// unrelated positions from different games, and the renderer's animations are
// written for a board that moved rather than a board that was replaced.

import { useEffect, useMemo, useRef, useState } from "react";

import { type TunerGame } from "src/engine/net/tuner";
import type { GameState } from "src/engine/rules/commands";
import { gameOf } from "src/engine/api/createGame";
import { boardFrom } from "../asciiBoard";
import { BoardCanvas, type BoardCanvasHandle } from "../BoardCanvas";
import { Btn, Panel, styled, Text, TOKENS } from "../kit";
import { hydrate, partsOn, resourcesOf, scrapOn } from "../snapshot";
import { gameCaption, teamLabel } from "../tunerView";
import { TEAM_TONE } from "./palette";
import { Legend, Row, Spacer, Tag, tagSkin } from "./parts";

/**
 * The board, edge to edge and behind everything.
 *
 * **`zIndex: -1`, and that is the whole of what keeps the page usable.**
 * `pointerEvents` is left alone on purpose — every handler here is a no-op, so
 * what the canvas buys is the camera, and dragging the free part of the page
 * turns the board it is showing. At `zIndex: 0` it bought rather more than
 * that: a *positioned* element with z-index 0 paints above the page's
 * non-positioned in-flow content, and hit testing follows painting, so a
 * full-viewport canvas swallowed every click, drag and wheel meant for the
 * cards. Negative paints above the scroller's own background and below its
 * content — which is exactly the split wanted, since the cards are the page and
 * the gaps are the board.
 */
const Behind = styled("div")({ position: "fixed", inset: 0, zIndex: -1 });

/**
 * What the board is, floating over it. Fixed rather than in the flow, because
 * the thing it labels is fixed — scrolling the cards must not carry the caption
 * off a board that has not moved.
 */
const Caption = styled(Panel)(({ theme }) => ({
  position: "fixed",
  zIndex: theme.kit.z.boardOverlay,
  top: TOKENS.layout.adminBar + 12,
  right: theme.kit.space.lg,
  maxWidth: "32vw",
  padding: `${theme.kit.space.sm}px ${theme.kit.space.md}px`,
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs
}));

const Side = styled(Text)({ display: "block", lineHeight: 1.6 });

export function BoardPanel({
  game,
  frame,
  onClose
}: {
  game: TunerGame;
  frame: "mid" | "final";
  onClose(): void;
}): React.ReactElement {
  const drawn = frame === "mid" ? game.mid : game.final;
  /**
   * The position, from the snapshot where there is one and from the drawing
   * otherwise.
   *
   * **The snapshot is the point and the drawing is the fallback.** A
   * reconstruction shows where the pieces stood; a snapshot shows what they
   * were holding, which way a loaded lane ran and how big a heap over nine
   * really was. Only one game per army carries one — see `TunerFrame.state`.
   *
   * Async, because gzip on the platform is: the fallback is drawn first and
   * replaced when the real thing arrives, so the panel never sits empty.
   */
  const fallback = useMemo(() => (drawn ? boardFrom(drawn.board) : null), [drawn]);
  const [exact, setExact] = useState<GameState | null>(null);
  useEffect(() => {
    setExact(null);
    if (!drawn?.state) return;
    let live = true;
    void hydrate(drawn.state).then((held) => {
      if (live) setExact(held);
    });
    return () => {
      live = false;
    };
  }, [drawn]);
  const state = exact ?? fallback;
  // A still board: its own `Game`, rebuilt only when the frame changes.
  const board = useMemo(() => (state ? gameOf(state) : null), [state]);
  const counts = useMemo(() => (exact ? resourcesOf(exact) : null), [exact]);
  const loose = useMemo(() => (exact ? scrapOn(exact) : 0), [exact]);
  const parts = useMemo(() => (exact ? partsOn(exact) : 0), [exact]);
  const canvas = useRef<BoardCanvasHandle>(null);

  /**
   * How much of the canvas the page is covering, in viewport pixels.
   *
   * **This is the whole mechanism, and it is the camera's rather than CSS's.**
   * The board is drawn edge to edge behind the page, so "centred" and "visible"
   * are different things the moment the cards are laid over one side of it —
   * `setViewInset` shifts the projection into the free area instead.
   *
   * Measured off the cards column rather than assumed to be a third: its width
   * answers to the breakpoint, so a hardcoded fraction would be wrong at every
   * size but the one it was written at. Re-measured on resize, since neither
   * the state nor React re-renders when the window changes.
   */
  useEffect(() => {
    if (!state) return;
    const apply = () => {
      const column = document.querySelector<HTMLElement>("[data-tuner-cards]");
      const covered = column ? column.getBoundingClientRect().right : 0;
      canvas.current?.setViewInset({
        left: Math.max(0, Math.round(covered)),
        right: 0,
        top: TOKENS.layout.adminBar,
        bottom: 0
      });
      // `setViewInset` re-fits itself while nothing has been played, which is
      // always true here — this board is a fresh `new-game` with no history —
      // so the fit is the inset's own doing rather than a second decision.
      canvas.current?.fitView();
    };
    // Twice: once after layout, once after `BoardCanvas`'s own ResizeObserver
    // has told the renderer how big it is. Idempotent — every one of these is a
    // camera *target*, not a write.
    const raf = requestAnimationFrame(apply);
    const timer = setTimeout(apply, 250);
    window.addEventListener("resize", apply);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      window.removeEventListener("resize", apply);
    };
  }, [state]);

  return (
    <>
      <Behind>
        {board ? (
          <BoardCanvas
            ref={canvas}
            key={`${game.id}-${frame}`}
            game={board}
            interactive={false}
          />
        ) : null}
      </Behind>

      <Caption>
        <Row>
          <Tag style={tagSkin(TEAM_TONE[game.outcome])}>{teamLabel(game)}</Tag>
          <Text tone="dim" size={11}>
            {frame === "mid" ? "midgame" : "final"} · ply {drawn?.ply ?? 0}
          </Text>
          <Spacer />
          <Btn small variant="ghost" aria-label="close the board" onClick={onClose}>
            ×
          </Btn>
        </Row>
        {counts && (
          // **The numbers a drawing cannot carry.** `held` above all: material
          // in a piece's banks appears nowhere in the picture, and "this army
          // ends its games sitting on forty units" is the sort of thing being
          // looked for when somebody asks whether a style is behaving.
          <div>
            {counts.map((row) => (
              <Side key={row.side} tone="cream" size={11}>
                <Text size={11} tone="dim">side {row.side}</Text>{" "}
                {row.army.map((entry) => `${entry.count}${entry.glyph}`).join(" ")}
                {" · holding "}
                <Text size={11} role="display" tone="brand" weight={700}>
                  {row.held}
                </Text>
              </Side>
            ))}
            <Side tone="dim" size={11}>
              {parts} parts on the board · {loose} scrap on the floor
            </Side>
          </div>
        )}
        <Text block tone="dim" size={10}>
          {!state ? (
            "This position could not be drawn."
          ) : exact ? (
            <Legend
              text="the real position"
              title="The real position"
              rule="Hydrated from a stored state rather than read back from the drawing, so every count is exact — held material, the direction of a loaded lane, a heap over nine. One game per army carries one."
            />
          ) : (
            <Legend
              text="rebuilt from the drawing"
              title="A reconstruction"
              rule="The gallery keeps positions as text, so this is a reconstruction. Where the pieces and scrap stand is exact; facing, held material, lane ownership and the direction of a loaded lane are not recorded and are guessed."
            />
          )}{" "}
          · {gameCaption(game)}
        </Text>
      </Caption>
    </>
  );
}
